import {
	PivotCompat as Pivot,
	PivotItem,
	SeparatorCompat as Separator,
	StackCompat as Stack,
} from 'components/FluentUICompat';
import React, {useMemo} from 'react';
import {DetailsField} from './DetailsSection';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';
import {useUserContext} from 'authentication/UserContext';

export interface TabsInitialFieldsProps {
	rows: DetailsField[][];
	renderField: (field: DetailsField, tooltipContent?: string) => JSX.Element;
	classnames?: {singleDetail?: string};
	generalFieldLabels?: string[];
	dateFieldLabels?: string[];
	referenceFieldLabels?: string[];
}

export const TabsInitialFields: React.FC<TabsInitialFieldsProps> = ({
	rows,
	renderField,
	classnames,
	generalFieldLabels = [],
	dateFieldLabels = [],
	referenceFieldLabels = [],
}) => {
	const {t} = useTranslation('components/detailssection');
	const {isAvk, isBav} = useUserContext();
	const allFields = useMemo(() => rows.flat(), [rows]);

	const labelSet = (labels: string[]) =>
		new Set(labels.map(x => x.trim()).filter(Boolean));

	const generalSet = useMemo(
		() => labelSet(generalFieldLabels),
		[generalFieldLabels],
	);
	const dateSet = useMemo(() => labelSet(dateFieldLabels), [dateFieldLabels]);
	const referenceSet = useMemo(
		() => labelSet(referenceFieldLabels),
		[referenceFieldLabels],
	);

	// --- URL → Selected Tab syncing ---
	const location = useLocation();
	const navigate = useNavigate();
	const query = new URLSearchParams(location.search);
	const pivotFromUrl = query.get('view');

	const GENERAL = 'GeneralTab';
	const DATE = 'DateTab';
	const REF = 'ReferenceTab';

	const [selectedPivotKey, setSelectedPivotKey] = React.useState(
		pivotFromUrl ?? GENERAL,
	);

	const onPivotLinkClick = async (item?: any) => {
		if (!item?.props.itemKey) return;

		const tabKey = item.props.itemKey;
		setSelectedPivotKey(tabKey);

		const params = new URLSearchParams(location.search);
		params.set('view', tabKey);
		await navigate({search: params.toString()}, {replace: true});
	};

	// --- Categorize fields ---
	const categorizedTabFields = useMemo(() => {
		const general: DetailsField[] = [];
		const date: DetailsField[] = [];
		const reference: DetailsField[] = [];

		allFields.forEach(field => {
			const label = field.label?.trim();
			if (!label) return;

			if (generalSet.has(label)) general.push(field);
			if (dateSet.has(label)) date.push(field);
			if (referenceSet.has(label)) reference.push(field);
		});

		return {general, date, reference};
	}, [allFields, generalSet, dateSet, referenceSet]);

	// Sort by rowNumber only
	const sortByRowNumber = (fields: DetailsField[]) =>
		[...fields].sort(
			(a, b) =>
				(a.rowNumber ?? Number.MAX_SAFE_INTEGER) -
				(b.rowNumber ?? Number.MAX_SAFE_INTEGER),
		);

	/**
	 * Split fields into rows based purely on rowNumber.
	 * All fields with the same rowNumber appear on the same horizontal row.
	 */
	const groupByRowNumber = (fields: DetailsField[]): DetailsField[][] => {
		const rows = new Map<number, DetailsField[]>();

		fields.forEach(field => {
			const row = field.rowNumber ?? Number.MAX_SAFE_INTEGER;
			if (!rows.has(row)) rows.set(row, []);
			rows.get(row)!.push(field);
		});

		return Array.from(rows.entries())
			.sort((a, b) => a[0] - b[0])
			.map(entry => entry[1]);
	};

	// Render a single tab
	const renderTab = (fields: DetailsField[]) => {
		const sorted = sortByRowNumber(fields);
		const grouped = groupByRowNumber(sorted);

		return grouped.map((row, index) => (
			<div key={index}>
				<Stack horizontal tokens={{childrenGap: 50}}>
					{row.map(field => (
						<div
							key={field.label}
							className={classnames?.singleDetail}
							style={{width: `${100 / row.length}%`}}
						>
							{renderField(field, field.tooltipContent)}
						</div>
					))}
				</Stack>
				<Separator />
			</div>
		));
	};

	return (
		<Pivot selectedKey={selectedPivotKey} onLinkClick={onPivotLinkClick}>
			<PivotItem itemKey={GENERAL} headerText={t('GeneralTabHeader')}>
				{renderTab(categorizedTabFields.general)}
			</PivotItem>

			{!(isAvk || isBav) && (
				<PivotItem itemKey={DATE} headerText={t('DateTabHeader')}>
					{renderTab(categorizedTabFields.date)}
				</PivotItem>
			)}

			<PivotItem itemKey={REF} headerText={t('ReferenceTabHeader')}>
				{renderTab(categorizedTabFields.reference)}
			</PivotItem>
		</Pivot>
	);
};
